import { SxProps, Theme } from "@mui/material";

export const modalStyle: SxProps<Theme> = (theme) => ({
  position: "fixed",
  inset: 0,
  width: "100vw",
  height: "100vh",
  maxWidth: "none",
  maxHeight: "none",
  overflowY: "auto",
  borderRadius: 0,
  border: "none",
  backgroundColor: theme.palette.quizPopap.quizBg,
  color: theme.palette.quizPopap.quizText,
  boxShadow: "none",
  p: { xs: 1.5, sm: 2, md: 2 },
});

export const topBarStyle: SxProps<Theme> = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 1,
  mb: { xs: 0.8, md: 1 },
  pr: { xs: 1, md: 2.5 },
};

export const topBarActionsStyle: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: 0.8,
  flexShrink: 0,
};

export const titleStyle: SxProps<Theme> = {
  fontSize: { xs: "20px", md: "32px" },
  fontWeight: 700,
};

export const subtitleStyle: SxProps<Theme> = {
  mt: 0.8,
  display: "flex",
  alignItems: "center",
  gap: 1.25,
  flexWrap: "wrap",
};

export const chipRowStyle: SxProps<Theme> = {
  display: "flex",
  flexWrap: "wrap",
  gap: 1,
  mt: { xs: 0.8, md: 0.9 },
  mb: 1.25,
};

export const statsGridStyle: SxProps<Theme> = {
  display: "grid",
  gridTemplateColumns: {
    xs: "repeat(2, minmax(0, 1fr))",
    md: "repeat(4, minmax(0, 1fr))",
  },
  gap: { xs: 1, md: 1.25 },
  mb: 1.25,
};

export const statCardStyle: SxProps<Theme> = (theme) => ({
  borderRadius: "14px",
  border: `1px solid ${theme.palette.quizPopap.quizSeparator}`,
  backgroundColor: theme.palette.mode === "dark" ? "#181E2D" : "#FFFFFF",
  p: { xs: 1.1, md: 1.2 },
  display: "flex",
  alignItems: "center",
  gap: { xs: 0.8, md: 1 },
});

export const statValueStyle: SxProps<Theme> = {
  fontSize: { xs: "34px", md: "36px" },
  lineHeight: 1.1,
  fontWeight: 900,
  color: "text.primary",
};

export const statLabelStyle: SxProps<Theme> = {
  fontSize: { xs: "13px", md: "14px" },
  opacity: 0.78,
};

export const statSubLabelStyle: SxProps<Theme> = {
  fontSize: { xs: "12px", md: "13px" },
  opacity: 0.58,
};

export const statIconWrapStyle = (color: string): SxProps<Theme> => ({
  width: 46,
  height: 46,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color,
  backgroundColor: `${color}1A`,
  flexShrink: 0,
});

export const assessmentFocusLabelStyle: SxProps<Theme> = (theme) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 1,
  borderRadius: "24px",
  minWidth: { xs: 220, md: 320 },
  height: 40,
  px: 1.4,
  backgroundColor: theme.palette.mode === "dark" ? "#151B2B" : "#FFFFFF",
  border: `1px solid ${theme.palette.quizPopap.quizSeparator}`,
  boxSizing: "border-box",
});

export const statusPillStyle: SxProps<Theme> = {
  display: "inline-flex",
  alignItems: "center",
  gap: 0.8,
  borderRadius: "999px",
  px: 1.4,
  py: 0.45,
  fontSize: "13px",
  fontWeight: 700,
  color: "#166534",
  backgroundColor: "transparent",
  border: "1px solid rgba(22,163,74,0.22)",
};

export const metaTextStyle: SxProps<Theme> = {
  fontSize: "13px",
  opacity: 0.62,
};

export const dashboardGridStyle: SxProps<Theme> = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", md: "1.3fr 1fr 1fr" },
  gap: 1.25,
};

export const overviewMiddleRowStyle: SxProps<Theme> = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", md: "2fr 1fr" },
  gap: 1.25,
  alignItems: "start",
};

export const sectionCardStyle: SxProps<Theme> = (theme) => ({
  borderRadius: "14px",
  border: `1px solid ${theme.palette.quizPopap.quizSeparator}`,
  backgroundColor: theme.palette.mode === "dark" ? "#181E2D" : "#FFFFFF",
  p: { xs: 1.2, md: 1.4 },
  minHeight: 140,
  display: "flex",
  flexDirection: "column",
});

export const currentQuestionCardStyle: SxProps<Theme> = (theme) => ({
  borderRadius: "14px",
  border: `1px solid ${theme.palette.quizPopap.quizSeparator}`,
  backgroundColor: theme.palette.mode === "dark" ? "#181E2D" : "#FFFFFF",
  p: { xs: 1.2, md: 1.4 },
  minHeight: "auto",
  alignSelf: "start",
  display: "flex",
  flexDirection: "column",
});

export const sectionTitleStyle: SxProps<Theme> = {
  fontSize: "14px",
  fontWeight: 800,
  mb: 1,
  color: "text.primary",
};

export const sectionBodyStyle: SxProps<Theme> = {
  flex: 1,
  display: "flex",
  flexDirection: "column",
  gap: 1,
  minHeight: 0,
};

export const sectionSubtitleStyle: SxProps<Theme> = {
  fontSize: "13px",
  color: "text.secondary",
  mb: 0.5,
};

export const sectionFootnoteStyle: SxProps<Theme> = {
  fontSize: "12px",
  color: "text.secondary",
  mt: 0.5,
};

export const metricsRowStyle: SxProps<Theme> = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: 1,
};

export const metricMiniCardStyle: SxProps<Theme> = (theme) => ({
  borderRadius: "12px",
  border: `1px solid ${theme.palette.quizPopap.quizSeparator}`,
  backgroundColor: theme.palette.mode === "dark" ? "#181E2D" : "#FFFFFF",
  p: 1.25,
  display: "flex",
  alignItems: "center",
  gap: 1,
});

export const metricMiniCardIconWrapStyle: SxProps<Theme> = {
  width: 34,
  height: 34,
  borderRadius: "10px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "rgba(14,165,233,0.12)",
  color: "#0EA5E9",
  flexShrink: 0,
};

export const metricMiniCardIconWrapSuccessStyle: SxProps<Theme> = {
  width: 34,
  height: 34,
  borderRadius: "10px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "rgba(34,197,94,0.12)",
  color: "#22C55E",
  flexShrink: 0,
};

export const metricMiniCardLabelStyle: SxProps<Theme> = {
  fontSize: "12px",
  color: "text.secondary",
  lineHeight: 1.1,
};

export const metricMiniCardValueStyle: SxProps<Theme> = {
  fontSize: "22px",
  fontWeight: 800,
  color: "text.primary",
  lineHeight: 1.1,
  whiteSpace: "nowrap",
};

export const sectionProgressStyle: SxProps<Theme> = {
  height: 8,
  borderRadius: 999,
  backgroundColor: "rgba(148,163,184,0.35)",
  "& .MuiLinearProgress-bar": {
    borderRadius: 999,
    backgroundColor: "#0EA5E9",
  },
};

export const progressMiniScrollWrapStyle: SxProps<Theme> = {
  width: "100%",
  overflowX: "auto",
};

export const progressMiniGridStyle: SxProps<Theme> = {
  display: "grid",
  gridTemplateColumns: "repeat(6, minmax(0, 1fr))",
  gap: 1,
  minHeight: { xs: 130, md: 148 },
  alignItems: "stretch",
};

export const progressMiniCardStyle: SxProps<Theme> = (theme) => ({
  borderRadius: "10px",
  border: `1px solid ${theme.palette.quizPopap.quizSeparator}`,
  backgroundColor: theme.palette.mode === "dark" ? "#181E2D" : "#FFFFFF",
  p: { xs: 1, md: 1.2 },
  minHeight: { xs: 108, md: 104 },
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 1,
});

export const progressMiniRingWrapStyle: SxProps<Theme> = {
  position: "relative",
  width: { xs: 50, md: 60 },
  height: { xs: 50, md: 60 },
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

export const progressMiniRingTrackStyle: SxProps<Theme> = {
  position: "absolute",
  color: "#E5E7EB",
};

export const progressMiniRingProgressStyle = (
  color: string,
): SxProps<Theme> => ({
  position: "absolute",
  color,
  "& .MuiCircularProgress-circle": {
    strokeLinecap: "round",
  },
});

export const progressMiniRingLabelStyle = (color: string): SxProps<Theme> => ({
  position: "relative",
  zIndex: 1,
  fontSize: { xs: "10px", md: "12px" },
  fontWeight: 800,
  color,
  lineHeight: 1,
});

export const performanceTableHeaderStyle: SxProps<Theme> = {
  display: "grid",
  gridTemplateColumns: "1fr 44px 28px",
  alignItems: "center",
  gap: 0.75,
  pb: 0.5,
  borderBottom: "1px solid rgba(148,163,184,0.25)",
};

export const performanceTableHeaderCellStyle: SxProps<Theme> = {
  fontSize: "11px",
  fontWeight: 700,
  color: "text.secondary",
};

export const performanceRowStyle: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: 0.55,
  pl: 0.6,
  py: 0.75,
  borderRadius: "10px",
};

export const performanceRowHighlightStyle: SxProps<Theme> = {
  backgroundColor: "rgba(239,68,68,0.08)",
};

export const performanceQuestionTopRowStyle: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: 0.75,
  minWidth: 0,
};

export const performanceMetricsRowStyle: SxProps<Theme> = {
  display: "grid",
  gridTemplateColumns: "1fr 44px 28px",
  alignItems: "center",
  gap: 0.75,
};

export const performanceQuestionStyle: SxProps<Theme> = {
  fontSize: "13px",
  fontWeight: 800,
  color: "text.primary",
};

export const performanceTypeChipStyle: SxProps<Theme> = {
  height: 20,
  fontSize: "10px",
  fontWeight: 700,
  "& .MuiChip-label": {
    px: 0.75,
  },
};

export const performancePercentStyle: SxProps<Theme> = {
  fontSize: "12px",
  fontWeight: 700,
  color: "text.primary",
  textAlign: "right",
};

export const performanceBarTrackStyle: SxProps<Theme> = {
  height: 8,
  borderRadius: 999,
  backgroundColor: "rgba(148,163,184,0.35)",
  overflow: "hidden",
};

export const performanceBarFillStyle = (color: string): SxProps<Theme> => ({
  height: "100%",
  borderRadius: 999,
  backgroundColor: color,
});

export const performanceStatusIconStyle = (color: string): SxProps<Theme> => ({
  width: 24,
  height: 24,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color,
  backgroundColor: `${color}1A`,
});

export const sectionLinkStyle: SxProps<Theme> = {
  fontSize: "13px",
  fontWeight: 700,
  color: "#0EA5E9",
  mt: 0.25,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: 0.35,
};

export const currentQuestionTitleStyle: SxProps<Theme> = {
  fontSize: "28px",
  fontWeight: 800,
  color: "text.primary",
  lineHeight: 1,
  mb: 0.35,
};

export const currentQuestionMetaStyle: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: 0.5,
  fontSize: "13px",
  color: "text.secondary",
  mb: 1,
};

export const bottomSectionGridStyle: SxProps<Theme> = {
  mt: 1.25,
  display: "grid",
  gridTemplateColumns: { xs: "1fr", md: "2.3fr 1fr" },
  gap: 1.25,
  alignItems: "start",
};

export const bottomPanelStyle: SxProps<Theme> = (theme) => ({
  borderRadius: "14px",
  border: `1px solid ${theme.palette.quizPopap.quizSeparator}`,
  backgroundColor: theme.palette.mode === "dark" ? "#181E2D" : "#FFFFFF",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  mt: 0,
});

export const bottomPanelHeaderStyle: SxProps<Theme> = {
  px: { xs: 1.2, md: 1.5 },
  py: 1.2,
};

export const bottomPanelTitleStyle: SxProps<Theme> = {
  fontSize: "14px",
  fontWeight: 800,
  color: "text.primary",
};

export const bottomPanelTitleMutedStyle: SxProps<Theme> = {
  fontSize: "13px",
  fontWeight: 500,
  color: "text.secondary",
};

export const participantsTableScrollStyle: SxProps<Theme> = {
  width: "100%",
  overflowX: "auto",
  WebkitOverflowScrolling: "touch",
  pb: 0.5,
};

export const participantsTableHeaderStyle: SxProps<Theme> = {
  display: "grid",
  gridTemplateColumns:
    "minmax(140px, 1.5fr) 110px minmax(100px, 1fr) 88px 110px 110px 100px 110px",
  minWidth: 980,
  gap: 1,
  px: { xs: 1.2, md: 1.5 },
  py: 0.85,
  borderBottom: "1px solid rgba(148,163,184,0.22)",
};

export const participantsTableHeaderCellStyle: SxProps<Theme> = {
  fontSize: "11px",
  fontWeight: 700,
  color: "text.secondary",
  whiteSpace: "nowrap",
};

export const participantsTableRowStyle: SxProps<Theme> = {
  display: "grid",
  gridTemplateColumns:
    "minmax(140px, 1.5fr) 110px minmax(100px, 1fr) 88px 110px 110px 100px 110px",
  minWidth: 980,
  gap: 1,
  alignItems: "center",
  px: { xs: 1.2, md: 1.5 },
  py: 0.8,
  borderBottom: "1px solid rgba(148,163,184,0.14)",
  "&:last-of-type": {
    borderBottom: "none",
  },
};

export const participantIdentityStyle: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: 0.9,
  minWidth: 0,
};

export const participantAvatarStyle = (color: string): SxProps<Theme> => ({
  width: 34,
  height: 34,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "12px",
  fontWeight: 800,
  color: "#FFFFFF",
  backgroundColor: color,
  flexShrink: 0,
});

export const participantNameStyle: SxProps<Theme> = {
  fontSize: "13px",
  fontWeight: 700,
  color: "text.primary",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

export const participantCellStyle: SxProps<Theme> = {
  fontSize: "12px",
  fontWeight: 600,
  color: "text.primary",
};

export const participantMutedCellStyle: SxProps<Theme> = {
  fontSize: "12px",
  color: "text.secondary",
};

export const participantStatusCompletedStyle: SxProps<Theme> = {
  display: "inline-flex",
  alignItems: "center",
  gap: 0.45,
  borderRadius: "999px",
  px: 0.9,
  py: 0.35,
  fontSize: "11px",
  fontWeight: 700,
  color: "#166534",
  backgroundColor: "rgba(34,197,94,0.14)",
  width: "fit-content",
};

export const participantStatusInProgressStyle: SxProps<Theme> = {
  display: "inline-flex",
  alignItems: "center",
  gap: 0.45,
  borderRadius: "999px",
  px: 0.9,
  py: 0.35,
  fontSize: "11px",
  fontWeight: 700,
  color: "#0369A1",
  backgroundColor: "rgba(14,165,233,0.14)",
  width: "fit-content",
};

export const participantStatusNotStartedStyle: SxProps<Theme> = {
  display: "inline-flex",
  alignItems: "center",
  gap: 0.45,
  borderRadius: "999px",
  px: 0.9,
  py: 0.35,
  fontSize: "11px",
  fontWeight: 700,
  color: "#4B5563",
  backgroundColor: "rgba(107,114,128,0.16)",
  width: "fit-content",
};

export const attentionListStyle: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  px: { xs: 1.2, md: 1.5 },
  py: 0.5,
};

export const attentionRowStyle: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 1,
  py: 1,
  borderBottom: "1px solid rgba(148,163,184,0.14)",
  "&:last-of-type": {
    borderBottom: "none",
  },
};

export const needsAttentionPanelStyle: SxProps<Theme> = {
  mt: { xs: 0, md: -0.6 },
};

export const needsAttentionPanelCombinedStyle: SxProps<Theme> = (theme) => ({
  borderRadius: "14px",
  border: `1px solid ${theme.palette.quizPopap.quizSeparator}`,
  backgroundColor: theme.palette.mode === "dark" ? "#181E2D" : "#FFFFFF",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  pt: { xs: 0, md: 1 },
});

export const needsAttentionHeaderStyle: SxProps<Theme> = {
  px: { xs: 1.2, md: 1.5 },
  py: 1.2,
};

export const needsAttentionRowStyle: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 1,
  py: 1,
};

export const attentionBadgeInactiveStyle: SxProps<Theme> = {
  borderRadius: "999px",
  px: 0.9,
  py: 0.35,
  fontSize: "11px",
  fontWeight: 700,
  color: "#DC2626",
  backgroundColor: "rgba(239,68,68,0.12)",
  whiteSpace: "nowrap",
};

export const attentionBadgeStuckStyle: SxProps<Theme> = {
  borderRadius: "999px",
  px: 0.9,
  py: 0.35,
  fontSize: "11px",
  fontWeight: 700,
  color: "#D97706",
  backgroundColor: "rgba(245,158,11,0.14)",
  whiteSpace: "nowrap",
};

export const bottomPanelFooterStyle: SxProps<Theme> = {
  px: { xs: 1.2, md: 1.5 },
  py: 1,
};
